import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Restore as RestoreIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../services/api';
import {
  DEFAULT_POOL_WEARABLE_BASELINE,
  mergePoolWearableBaselineDefaults,
  POOL_WEARABLE_BASELINE_INTRO,
  WEARABLE_BASELINE_METRIC_LABELS,
  type PoolWearableBaselineDefaults,
  type WearableBaselineMetricKey,
} from '../../constants/poolWearableBaselineDefaults';

type Props = {
  poolId: number | string | undefined;
  compact?: boolean;
  showSnackbar?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
};

const METRIC_KEYS = Object.keys(WEARABLE_BASELINE_METRIC_LABELS) as WearableBaselineMetricKey[];

const PoolWearableBaselineDefaultsCard: React.FC<Props> = ({ poolId, compact, showSnackbar }) => {
  const [defaults, setDefaults] = useState<PoolWearableBaselineDefaults>(
    DEFAULT_POOL_WEARABLE_BASELINE
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  const load = useCallback(async () => {
    if (!poolId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ defaults?: Partial<PoolWearableBaselineDefaults> }>(
        `/api/pools/${poolId}/wearable-baseline-defaults`,
        undefined,
        { useCache: false }
      );
      setDefaults(mergePoolWearableBaselineDefaults(res?.defaults));
    } catch (e) {
      console.error(e);
      setError('생체 기준값을 불러오기 실패');
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    load();
  }, [load]);

  const setMetric = (key: WearableBaselineMetricKey, field: 'p10' | 'p50' | 'p90', value: number) => {
    setDefaults((prev) => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [key]: { ...prev.metrics[key], [field]: value },
      },
    }));
  };

  const handleSave = async () => {
    if (!poolId) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/pools/${poolId}`, {
        wearable_baseline_defaults: defaults,
      });
      api.invalidateGetCache(`/api/pools/${poolId}`);
      api.invalidateGetCache(`/api/pools/${poolId}/wearable-baseline-defaults`);
      showSnackbar?.('웨어러블 생체 기준값을 저장함', 'success');
      await load();
    } catch (e) {
      console.error(e);
      setError('저장에 실패');
      showSnackbar?.('생체 기준값 저장에 실패', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreFactory = () => {
    setDefaults(mergePoolWearableBaselineDefaults(null));
    showSnackbar?.('서버 기본값으로 폼을 채움. 저장하면 반영됨', 'info');
  };

  if (!poolId) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: compact ? 2 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            웨어러블 생체·바이오리듬 기준값
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {POOL_WEARABLE_BASELINE_INTRO}
          </Typography>
        </Box>
        {compact ? (
          <Button size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '접기' : '펼치기'}
          </Button>
        ) : null}
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={defaults.enabled}
                    onChange={(e) => setDefaults((p) => ({ ...p, enabled: e.target.checked }))}
                  />
                }
                label="시설 기준값 사용"
              />
              <TextField
                size="small"
                type="number"
                label="개인 기준 최소 샘플 수"
                value={defaults.min_samples_for_personal}
                onChange={(e) =>
                  setDefaults((p) => ({
                    ...p,
                    min_samples_for_personal: Number(e.target.value),
                  }))
                }
                inputProps={{ min: 3, max: 30, step: 1 }}
                sx={{ width: 180 }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              지표별 참조 백분위 (p10 · p50 · p90)
            </Typography>
            <Grid container spacing={2}>
              {METRIC_KEYS.map((key) => (
                <Grid item xs={12} md={6} key={key}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      {WEARABLE_BASELINE_METRIC_LABELS[key]} ({defaults.metrics[key].unit})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {(['p10', 'p50', 'p90'] as const).map((field) => (
                        <TextField
                          key={field}
                          size="small"
                          type="number"
                          label={field}
                          fullWidth
                          value={defaults.metrics[key][field]}
                          onChange={(e) => setMetric(key, field, Number(e.target.value))}
                          inputProps={{ step: key === 'motion' ? 0.1 : 1 }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              집단 fallback (개인·동의 없을 때 절대 임계)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="HR 하한 (bpm)"
                  value={defaults.population_fallback.hr_low_threshold}
                  onChange={(e) =>
                    setDefaults((p) => ({
                      ...p,
                      population_fallback: {
                        ...p.population_fallback,
                        hr_low_threshold: Number(e.target.value),
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="HR 상한 (bpm)"
                  value={defaults.population_fallback.hr_high_threshold}
                  onChange={(e) =>
                    setDefaults((p) => ({
                      ...p,
                      population_fallback: {
                        ...p.population_fallback,
                        hr_high_threshold: Number(e.target.value),
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="SpO₂ 하한 (%)"
                  value={defaults.population_fallback.spo2_threshold}
                  onChange={(e) =>
                    setDefaults((p) => ({
                      ...p,
                      population_fallback: {
                        ...p.population_fallback,
                        spo2_threshold: Number(e.target.value),
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="움직임 임계"
                  value={defaults.population_fallback.motion_threshold}
                  onChange={(e) =>
                    setDefaults((p) => ({
                      ...p,
                      population_fallback: {
                        ...p.population_fallback,
                        motion_threshold: Number(e.target.value),
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              size="small"
              label="비고"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={defaults.notes}
              onChange={(e) => setDefaults((p) => ({ ...p, notes: e.target.value }))}
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                기준값 저장
              </Button>
              <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleRestoreFactory}>
                서버 기본값으로 채우기
              </Button>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default PoolWearableBaselineDefaultsCard;
